"use client";

import { useEffect } from "react";
import { useZipCode } from "@directv-digital/dds.server.application-context";
import {
  loadCookies,
  handleMissingZipRsn,
  trackImpressions,
  postPageLoadEffects,
  suppressAutoPageLoad
} from "./client-helper";

import { getCookie } from "@directv-digital/dds.services.cookie-service";
import { fireModifiedComponentTypePageLoadEvent } from "@directv-digital/dds.services.analytics-service";
import { fireDynamicComponentNotDisplayedEvent } from "lib/helper/analytics/fire-dynamic-component-not-displayed";
import { trackCartSummary } from "src/utils/cart-summary/fire-cart-summary";
import { runAnalyticsTask } from "lib/service/analytics-defer/analytics-defer";
import { deleteCookie } from "src/utils/deleteCookie";
import { ImpressionsData } from "lib/helper/build-impressions-data";

interface IPageBootstrapClientProps {
  reqQueryParams: Record<string, string>;
  evolvUUID?: string;
  page?: string;
  pagePath?: string;
  cookieList?: any;
  // WAT-10651: server-side pre-extracted analytics names for personalized
  // components. Previously this was the full Storyblok component tree
  // (`components?: any[]`), which bloated the RSC payload. The server now
  // filters by `personalizationKey || props.analytics.isPersonalizedComponent`
  // and maps to `props.analytics.contentFriendlyName` before passing.
  personalizedComponentNames?: string[];
  // WAT-10899: typeless personalized slots (no type+props.type) that resolved to no component.
  // Pre-extracted server-side to avoid passing the full CMS tree to the client (WAT-10651 pattern).
  typelessPlaceholderNames?: string[];
  cmsType?: string;
  // WAT-10897: sendImpressions payload pre-computed server-side
  impressionsData?: ImpressionsData;
  /**
   * WAT-11358: whether this page displays a cart. Defaults to true because the
   * CMS-driven `[...slug]` routes — which are nearly every page rendering this
   * component — can show the header cart. Pass false from routes that render no
   * header, so a cart persisted in localStorage from an earlier visit does not
   * report a `cart_summary` for a page the user cannot see a cart on.
   */
  enableCartSummary?: boolean;
  /**
   * WAT-11358: `svodsData` from the server-fetched genre cart story, used to name a
   * genre pack's included SVODs in cart_summary. A plain serialisable object, so it
   * costs a few hundred bytes of RSC payload and no client request.
   */
  cartSvodsData?: Record<string, any>;
}

const PageBootstrapClient = (props: IPageBootstrapClientProps) => {
  const {
    page,
    pagePath,
    reqQueryParams,
    evolvUUID,
    cookieList,
    cmsType: _cmsType,
    personalizedComponentNames,
    typelessPlaceholderNames,
    impressionsData,
    enableCartSummary = true,
    cartSvodsData
  } = props;
  const { update: updateZipCode = () => undefined } = useZipCode() ?? {};

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];

    // WAT-10897: suppress DMI's auto page-load at mount, before the deferred
    // trackImpressions below — see suppressAutoPageLoad() for the full rationale.
    suppressAutoPageLoad();

    loadCookies({
      reqQueryParams,
      cookieList
    });

    if (!getCookie("rsnFee")) {
      handleMissingZipRsn({ globalZipCode: { update: updateZipCode } });
    }

    const trackImpressionsArgs = {
      reqQueryParams,
      page,
      pagePath,
      root: "",
      // WAT-10897: feed sendImpressions the server-computed payload so it doesn't early-return
      ...impressionsData
    };

    // Defer 3rd-party tags until first user interaction OR 3s fallback.
    // Matches Page Router pattern (pages/index.tsx:264-300) to protect INP.
    let thirdPartyLoaded = false;
    let thirdPartyTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
    // WAT-11358: ids for the deferred analytics callback below, so the cleanup can
    // cancel it. Pages Router parity — pages/index.tsx:306-312 captures the same
    // two and cancels at :341-346. Without this, a soft navigation inside the idle
    // window leaves the callback to run against the UNMOUNTED page's props while
    // window.location.pathname already reads the destination: trackCartSummary
    // claims the destination's dedupe slot (fire-cart-summary.ts:106-113) using the
    // departed page's cartIsDisplayed and svodsData, and the destination's own call
    // is then swallowed as a duplicate.
    let analyticsIdleId: any;
    let analyticsTimeoutId: ReturnType<typeof setTimeout> | undefined = undefined;
    const interactionEvents = ["click", "scroll", "keydown", "touchstart"];
    const loadThirdPartyTags = () => {
      if (thirdPartyLoaded) {
        return;
      }
      thirdPartyLoaded = true;
      if (thirdPartyTimeout) {
        clearTimeout(thirdPartyTimeout);
      }
      interactionEvents.forEach(evt => window.removeEventListener(evt, loadThirdPartyTags));
      window.dataLayer = window.dataLayer || [];
      // Rides the shared post-paint analytics queue — this fires inside the
      // first-interaction listener, i.e. in the INP-critical frame.
      runAnalyticsTask(() => window.dataLayer.push({ event: "marTechTrigger" }));
      console.debug("<< marTechTrigger Triggered >>", document.readyState);
    };

    const windowLoadCallBack = () => {
      const fireAnalytics = () => {
        // WAT-10651: server-side pre-extracted (was a client-side filter+map
        // over the full Storyblok component tree). Falsy entries are still
        // ignored before firing — semantics preserved.
        if (personalizedComponentNames && personalizedComponentNames.length > 0) {
          personalizedComponentNames.forEach(component => {
            if(component) {
              fireModifiedComponentTypePageLoadEvent(component);
            }
          });
        }

        // WAT-10899: fire not-displayed event inside the same deferred callback as displayed events
        // (parity with pages/index.tsx:253-267 where both fire inside triggerAnalytics → requestIdleCallback).
        // Names are pre-extracted server-side to avoid sending the full CMS tree to the client (WAT-10651 pattern).
        typelessPlaceholderNames?.forEach(name => {
          fireDynamicComponentNotDisplayedEvent(name);
        });

        // WAT-11358: GA4 view_cart / cart_summary. Once per page load, suppressed
        // when the cart is empty. Mirrors pages/index.tsx `triggerAnalytics`.
        // Called on every page load even when this bootstrap is shared by a route
        // that renders no header — the trigger needs to see cart-less loads to
        // advance its dedupe key, otherwise a later return to a cart page reads
        // as a re-render and is swallowed.
        trackCartSummary({
          cartIsDisplayed: enableCartSummary,
          svodsData: cartSvodsData
        });
      };

      if ("requestIdleCallback" in window) {
        analyticsIdleId = requestIdleCallback(
          () => {
            fireAnalytics();
          },
          { timeout: 2000 }
        );
      } else {
        analyticsTimeoutId = setTimeout(() => {
          fireAnalytics();
        }, 0);
      }

      // First-party impressions self-idle via requestIdleCallback inside trackImpressions.
      trackImpressions(trackImpressionsArgs);
    };

    // Register 3rd-party deferral OUTSIDE windowLoadCallBack so interactions
    // happening between hydration and window.load still trigger the defer,
    // and the 3s fallback is measured from effect mount (Page Router parity:
    // pages/index.tsx:264-286 registers immediately in its useEffect body).
    interactionEvents.forEach(evt => window.addEventListener(evt, loadThirdPartyTags, {
      once: true,
      passive: true
    }));
    thirdPartyTimeout = setTimeout(loadThirdPartyTags, 3000);

    console.debug("<< Event listener added");
    if (document.readyState === "complete") {
      windowLoadCallBack();
    } else {
      window?.addEventListener("load", windowLoadCallBack);
    }

    postPageLoadEffects({
      evolvUUID
    });

    // US: Delete cookie "intent" if found in any page of Upper Funnel for shop team WAT-5799

    /**
     * @see {@link https://directv-it.atlassian.net/browse/WAT-8043}
     */
    const params = new URLSearchParams(window?.location?.search);
    if (params?.get("retainCookie") !== "true") {
      deleteCookie("intent");
    }

    return () => {
      window.removeEventListener("load", windowLoadCallBack);
      if (thirdPartyTimeout) {
        clearTimeout(thirdPartyTimeout);
      }
      // WAT-11358: cancel the deferred analytics callback — see the ids above.
      if (analyticsIdleId && "cancelIdleCallback" in window) {
        cancelIdleCallback(analyticsIdleId);
      }
      if (analyticsTimeoutId) {
        clearTimeout(analyticsTimeoutId);
      }
      interactionEvents.forEach(evt => window.removeEventListener(evt, loadThirdPartyTags));
    };
  }, []);

  // set UF navigation history in session storage for shop team WAT-5710
  useEffect(() => {
    try {
      const currentPage = window.location.href;
      const history = JSON.parse(
        sessionStorage.getItem("navigationHistory") || "[]"
      );
      if (
        history?.[history?.length - 1] !== currentPage &&
        !props?.page?.includes?.("/modal")
      ) {
        history.push(currentPage);
      }
      sessionStorage.setItem(
        "navigationHistory",
        JSON.stringify(history || [])
      );
    } catch (error) {
      console.error("Error updating navigation history:", error);
    }
  }, [props?.page]);

  return null;
};

export default PageBootstrapClient;
